import type { TFunction } from "react-i18next";
import { isBlank } from "utils/string";
import { isEmptyArray } from "utils/isEmptyArray";
import { FormState } from "../types";

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 500;

export type FormErrors = {
  name?: string;
  description?: string;
  storageType?: string;
  defaultDefinition?: string;
  predicates?: Array<{
    variableIdList?: string;
    definitionId?: string;
  }>;
};

export type FormTouched = {
  name?: boolean;
  description?: boolean;
  storageType?: boolean;
  defaultDefinition?: boolean;
  predicates?: Array<{
    variableIdList?: boolean;
    definitionId?: boolean;
  }>;
};

export function validate(
  values: FormState,
  t: TFunction,
  enableDefault: boolean
): FormErrors {
  const errors: FormErrors = {};

  if (isBlank(values.name)) {
    errors.name = t("form.name.required");
  } else if (values.name.trim().length > NAME_MAX_LENGTH) {
    errors.name = t("form.name.maxLength", { max: NAME_MAX_LENGTH });
  }

  if (
    !isBlank(values.description) &&
    values.description.length > DESCRIPTION_MAX_LENGTH
  ) {
    errors.description = t("form.description.maxLength", {
      max: DESCRIPTION_MAX_LENGTH,
    });
  }

  if (isBlank(values.storageType)) {
    errors.storageType = t("form.storageType.required");
  }

  if (enableDefault && isBlank(values.defaultDefinition)) {
    errors.defaultDefinition = t("form.defaultDefinition.required");
  }

  const predicateErrors = values.predicates.map(p => {
    const errors: FormErrors["predicates"][number] = {};

    if (isEmptyArray(p.variableIdList)) {
      errors.variableIdList = t("form.predicates.variableId.required");
    }

    if (p.definitionId !== null && isBlank(p.definitionId)) {
      errors.definitionId = t("form.predicates.definitionId.required");
    }

    return errors;
  });

  if (predicateErrors.some(e => Object.keys(e).length > 0)) {
    errors.predicates = predicateErrors;
  }

  return errors;
}

export function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}
